/// Port of mobile/lib/settings/data/settings_local_data.dart.
///
/// The storage keys deliberately match the Flutter SharedPreferences keys, so
/// the two clients stay conceptually in sync even though the stores differ.

import { resolveLocale } from '@/app/i18n/localesCatalog';
import { supportedLocales, type SupportedLocale } from '@/app/i18n/resources';
import {
  defaultMusicVolume,
  defaultSfxVolume,
} from '@/game/audio/audioConstants';
import {
  defaultDuckKey,
  defaultJumpKey,
  isBindableKey,
  normalizeKey,
} from './keyBindings';

export const themeKey = 'themeMode';
export const localeKey = 'localeCode';
export const musicEnabledKey = 'musicEnabled';
export const sfxEnabledKey = 'sfxEnabled';
export const musicVolumeKey = 'musicVolume';
export const sfxVolumeKey = 'sfxVolume';
export const controlsSwappedKey = 'controlsSwapped';
export const jumpKeyKey = 'jumpKey';
export const duckKeyKey = 'duckKey';

export type AppTheme = 'light' | 'dark';

function readString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Safari in private mode throws on any localStorage access.
    return null;
  }
}

function writeString(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Non-fatal: the setting just will not survive a reload.
  }
}

function readBool(key: string, fallback: boolean): boolean {
  const raw = readString(key);
  if (raw === null) return fallback;
  return raw === 'true';
}

function writeBool(key: string, value: boolean): void {
  writeString(key, value ? 'true' : 'false');
}

/// True once the user picks Light/Dark in Settings. Until then we follow the browser.
export function hasStoredTheme(): boolean {
  const raw = readString(themeKey);
  return raw === 'light' || raw === 'dark';
}

export function readSystemTheme(): AppTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/// Stored choice if set; otherwise the browser / OS color scheme.
export function readTheme(): AppTheme {
  const raw = readString(themeKey);
  if (raw === 'light' || raw === 'dark') return raw;
  return readSystemTheme();
}

export function writeTheme(theme: AppTheme): void {
  writeString(themeKey, theme);
}

export function readLocale(): SupportedLocale {
  const resolved = resolveLocale(
    readString(localeKey),
    supportedLocales,
    typeof navigator === 'undefined' ? undefined : navigator.language,
  );
  return (supportedLocales.find((locale) => locale === resolved) ?? 'en') as SupportedLocale;
}

export function writeLocale(locale: SupportedLocale): void {
  writeString(localeKey, locale);
}

export function readMusicEnabled(): boolean {
  return readBool(musicEnabledKey, true);
}

export function writeMusicEnabled(enabled: boolean): void {
  writeBool(musicEnabledKey, enabled);
}

export function readSfxEnabled(): boolean {
  return readBool(sfxEnabledKey, true);
}

export function writeSfxEnabled(enabled: boolean): void {
  writeBool(sfxEnabledKey, enabled);
}

function clampVolume(volume: number): number {
  return Math.min(1, Math.max(0, volume));
}

function readFloat(key: string, fallback: number): number {
  const raw = readString(key);
  if (raw === null) return fallback;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? clampVolume(parsed) : fallback;
}

function writeFloat(key: string, value: number): void {
  writeString(key, String(clampVolume(value)));
}

export function readMusicVolume(): number {
  return readFloat(musicVolumeKey, defaultMusicVolume);
}

export function writeMusicVolume(volume: number): void {
  writeFloat(musicVolumeKey, volume);
}

export function readSfxVolume(): number {
  return readFloat(sfxVolumeKey, defaultSfxVolume);
}

export function writeSfxVolume(volume: number): void {
  writeFloat(sfxVolumeKey, volume);
}

export function readControlsSwapped(): boolean {
  return readBool(controlsSwappedKey, false);
}

export function writeControlsSwapped(swapped: boolean): void {
  writeBool(controlsSwappedKey, swapped);
}

function readStoredKey(storageKey: string, fallback: string): string {
  const raw = readString(storageKey);
  if (raw === null) return fallback;
  const key = normalizeKey(raw);
  return isBindableKey(key) ? key : fallback;
}

export function readJumpKey(): string {
  return readStoredKey(jumpKeyKey, defaultJumpKey);
}

export function writeJumpKey(key: string): void {
  writeString(jumpKeyKey, normalizeKey(key));
}

export function readDuckKey(): string {
  return readStoredKey(duckKeyKey, defaultDuckKey);
}

export function writeDuckKey(key: string): void {
  writeString(duckKeyKey, normalizeKey(key));
}
